import { useEffect, useRef, useState } from 'react';
import {
  CaretDown,
  CaretUp,
  CheckCircle,
  ClipboardText,
  Flag,
  FlagCheckered,
  Link as LinkIcon,
  MagnifyingGlass,
  Minus,
  NotePencil,
  Question,
  StopCircle,
  Target,
  type Icon,
} from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';
import {
  useResearchCitations,
  useResearchTimeline,
  type ResearchStageKey,
  type ResearchTimelineEvent,
} from '~/hooks/queries/use-research';
import { useCancelResearch } from '~/hooks/mutations/use-cancel-research';
import { useFinishResearch } from '~/hooks/mutations/use-finish-research';
import { finishNowState } from './finish-now-gate';
// FRONT-2036 fix round 1 (item 14): `formatElapsed`/`hostOf`/`safeSourceHref`
// are the one shared copy in `research-display.ts` now — imported here,
// `sources-block.tsx`, and the phone's research panel/strip/briefing-reader.
// `formatElapsed` is re-exported below so `chat-research-banner.test.tsx`'s
// existing `import { formatElapsed } from './chat-research-banner'` keeps
// working unchanged (same re-export precedent `plan-status.ts` set for
// `message-bubble.tsx`).
import { formatElapsed, hostOf, safeSourceHref } from './research-display';

/**
 * FRONT-1578 — the five real engine stages, in the order the run moves
 * through them, and the user-facing label for each.
 *
 * Two of these — `reviewing` and `filling` — exist only on the DEEP research
 * pipeline (`deep.ts`); the standard pipeline (`engine.ts`) can never reach
 * them, so they are filtered out of the rendered strip entirely for a run
 * that isn't known to be deep (see `hasDeepEvidence`/`DEEP_ONLY_DISPLAY_STAGES`
 * at the call site — FRONT-1578 review finding 2). For a run that IS deep,
 * `filling` (gap-filling) is additionally CONDITIONAL within that pipeline:
 * a deep run that found no gaps to review skips it outright, so its absence
 * from that run's own stage history is a legitimate "skipped," not lost data
 * — see {@link stageStatuses}.
 */
const STAGE_ORDER: ResearchStageKey[] = [
  'planning',
  'researching',
  'reviewing',
  'filling',
  'writing',
];

/**
 * FRONT-1578 review finding 2: the two stages only the DEEP pipeline can
 * ever reach — filtered out of the displayed strip unless the run is KNOWN
 * to be deep. Showing them as upcoming steps on an ordinary (standard) run
 * would invent progress that run can never make, which the governing rule
 * for this whole surface forbids.
 */
const DEEP_ONLY_DISPLAY_STAGES = new Set<ResearchStageKey>(['reviewing', 'filling']);

const STAGE_LABELS: Record<ResearchStageKey, string> = {
  planning: 'Planning',
  researching: 'Researching',
  reviewing: 'Reviewing gaps',
  filling: 'Filling gaps',
  writing: 'Writing',
};

type StageStatus = 'done' | 'now' | 'upcoming' | 'skipped';

/**
 * Per-stage status for the strip, computed HONESTLY off the run's own
 * durable stage history (`kind: 'stage'` timeline rows) rather than
 * inferred from position — a run can legitimately skip `filling`, and
 * inferring "done" from "earlier than the current stage" would render that
 * skip as a false completion. Only a stage this run's timeline actually
 * recorded reaching counts as `done`; every other stage earlier than `stage`
 * is `skipped`, never `done`.
 *
 * Returns `null` when there is no stage to show a strip for at all — an
 * absent or unrecognised `stage` (a label a future engine version added that
 * this build doesn't know about yet) degrades to "no strip," never a wrong
 * one. Also returns `null` when the timeline history hasn't SETTLED at
 * least once yet (`historyKnown` false) — see the call site's own comment.
 * Before that, `events` is an empty array indistinguishable from "this run's
 * timeline is genuinely empty," and computing `skipped` off it would strike
 * through every earlier stage on every reload mid-run, honest history or
 * not (FRONT-1578 review finding 1).
 *
 * FRONT-1601: `finished` only ever drops the `now` reading — the currently
 * active stage was, by definition, actually reached, so it collapses into
 * `done` rather than staying "current" once there is no run left to be
 * current in. A `skipped` stage STAYS `skipped`: this is deliberately NOT
 * "mark everything done," because a stage this run genuinely bypassed (the
 * gap-review/gap-fill wave a "Finish now" run cuts short, or a conditional
 * stage this run found nothing for) never ran, and showing it as done would
 * invent progress the briefing does not actually rest on — the same class
 * of falsehood as a phantom "we searched and found nothing" claim. An
 * `upcoming` stage — one the run never reached and, now that it's over,
 * never will — reads as `skipped` too for the identical reason: "upcoming"
 * on a finished run is its own claim that more work might still happen,
 * which is no longer true. So a finished strip only ever shows two
 * readings, `done` or `skipped`, never `now` or `upcoming`. The running case
 * (below) is untouched, so a caller that never passes `finished` sees
 * byte-identical output to before this flag existed.
 */
function stageStatuses(
  stage: ResearchStageKey | undefined,
  events: ResearchTimelineEvent[],
  historyKnown: boolean,
  finished: boolean,
): Record<ResearchStageKey, StageStatus> | null {
  if (!stage || !STAGE_ORDER.includes(stage)) return null;
  if (!historyKnown) return null;
  const reached = new Set<ResearchStageKey>();
  for (const event of events) {
    if (event.kind === 'stage' && event.stage && STAGE_ORDER.includes(event.stage)) {
      reached.add(event.stage);
    }
  }
  const nowIndex = STAGE_ORDER.indexOf(stage);
  const statuses = {} as Record<ResearchStageKey, StageStatus>;
  STAGE_ORDER.forEach((key, index) => {
    if (finished) {
      // Reached (including the current stage, which was reached by
      // definition) is `done`; everything else — whether it would have
      // read `skipped` (earlier, genuinely bypassed) or `upcoming` (later,
      // never started) mid-run — reads `skipped` now that neither can ever
      // change again.
      statuses[key] = key === stage || reached.has(key) ? 'done' : 'skipped';
    } else if (key === stage) statuses[key] = 'now';
    else if (reached.has(key)) statuses[key] = 'done';
    else if (index < nowIndex) statuses[key] = 'skipped';
    else statuses[key] = 'upcoming';
  });
  return statuses;
}

// Re-exported so `chat-research-banner.test.tsx`'s existing
// `import { formatElapsed } from './chat-research-banner'` keeps working
// unchanged — see the import at the top of this file for the real source.
export { formatElapsed };

/** Quiet, per-kind icon for a work-log row — distinguishes WHAT happened
 *  without reaching for a state color (this is a log, not a status). */
const KIND_ICON: Record<ResearchTimelineEvent['kind'], Icon> = {
  plan: ClipboardText,
  search: MagnifyingGlass,
  'gap-check': Target,
  stage: Flag,
  note: NotePencil,
};

/** `research_events.kind` is a plain text column with no CHECK constraint —
 *  a future kind the frontend doesn't know about yet (or a row written by a
 *  version skew) must still render a row rather than crash the chat window
 *  with "Element type is invalid" on an `undefined` icon component. */
const FALLBACK_KIND_ICON: Icon = Question;

/**
 * FRONT-694 (epic FRONT-626) — the "deep research is running" surface.
 *
 * Deep research is the agreed exception to inline execution (ADR-0032): it can't
 * run inside the chat without freezing it, so it moves to the background research
 * engine. From the user's side that used to be a silent black box; this makes the
 * run visible — and, since FRONT-1500, watchable and stoppable.
 *
 * Design = handoff Option 2B: a distinct COBALT identity so it reads instantly
 * as a Deep-Research-only surface, separate from the lavender/`primary` "EDDY
 * is still working" banner. (FRONT-1578, second pass: the product owner
 * rejected the original turquoise/teal and specified cobalt blue instead —
 * same identity role, new hue — see the `--color-cobalt-*` ramp in app.css.)
 *
 * On the colour rule (CLAUDE.md): cobalt here is a deliberate *identity*
 * accent for the deep-research surface, NOT a success/destructive/warning
 * STATE — so the one-green/one-red state-token rule doesn't apply (same
 * spirit as the documented vendor-brand exception). The live "In progress"
 * dot is part of that same identity rather than a state either: the product
 * owner specified its own colour (#7FA8F2 / `cobalt-400`) for it directly,
 * rather than the `success` token it used before this swap. Its slow pulse
 * is still `motion-safe:` so it respects reduced-motion.
 *
 * ## Two states: a slim bar, or the details over the whole chat (FRONT-1500)
 *
 * The live detail (stage, work log, sources, Stop) used to render inline under
 * the strip, permanently, squeezed into a band above the conversation. It was
 * cramped — a 40-search run in a 160px box — and it ate the top of the chat for
 * the whole run whether the user was watching it or not. The product owner
 * asked for the opposite trade: give the detail the WHOLE chat, but only when
 * the user actually asks for it.
 *
 * So there are exactly two states, and the bar is the switch between them:
 *
 *  - **Collapsed (the default).** One cobalt bar: the deep-research identity,
 *    a slowly-pulsing "In progress" dot, and an invitation to click. It
 *    costs the conversation ~44px and never moves.
 *  - **Expanded.** The same bar, still in the same place, with the full detail
 *    panel filling everything below it — the explanation, the current stage,
 *    the work log, the sources taken in so far, and Stop. Clicking the bar
 *    again collapses it, and so does Escape (handled by the modal, which owns
 *    the key and the precedence — see `escapeAction`).
 *
 * The expanded panel is `absolute inset-0`, so **the caller must give it a
 * `relative` positioned parent that spans the conversation area** (in
 * `inbox-chat-modal.tsx` that's the wrapper around this banner and the scroll
 * column). Without one it would escape to the modal's backdrop and cover the
 * whole viewport. The collapsed bar sits at the top of that same box, which is
 * why the bar doesn't appear to move when the panel opens over the chat.
 *
 * Neither state is dismissable: the surface exists for as long as the run does
 * and clears itself when the run ends, which is the only correct time for it to
 * go away. (The pre-FRONT-1500 "Got it" acknowledgement is gone — it hid the
 * live half along with the sentence it acknowledged, which is the black box
 * this whole surface exists to remove. Now nothing hides the run; collapsing
 * just puts it back in one line.)
 *
 * `phase` carries the research engine's current stage label ("Planning the
 * research…", "Researching…", "Reviewing gaps…", "Writing the briefing…"),
 * mirrored live off `research_runs.phase` via `useResearch`; absent on a
 * pre-first-stage run, in which case a neutral placeholder shows instead —
 * deliberately WITHOUT the `research-phase` test id, so a check that waits for
 * a real stage can't be satisfied by the placeholder.
 *
 * The work log itself is the run's durable timeline (`useResearchTimeline`,
 * one row per plan/search/gap-check/stage/note event, newest last). The source
 * list is `useResearchCitations` — the SAME hook/list the Inspector's running
 * card and the finished briefing's Sources block use, so the count and the URLs
 * here can never disagree with what is shown elsewhere or with what the
 * finished briefing lists.
 */
interface ChatResearchBannerBaseProps {
  /** Whether a deep-research run (real or optimistically-assumed) is active
   *  for this chat. Never affected by anything the user clicks. */
  show: boolean;
  /** Whether the detail panel is open over the conversation. */
  expanded?: boolean;
  /** Toggle the detail panel. Optional so a caller that only wants the bar
   *  renders it as a plain, non-interactive strip. */
  onToggle?: () => void;
  phase?: string;
  /** The running run's numeric id. Absent during the brief optimistic window
   *  before the run row exists, where there is nothing yet to poll or stop. */
  runId?: number;
  /** FRONT-1578: the engine's current stage, as a stable key — drives the
   *  stage strip. Absent on an older run or an unrecognised value renders NO
   *  strip at all, never a wrong one (see {@link stageStatuses}). FRONT-1601
   *  FIX 1: also absent for every FINISHED run — the backend nulls
   *  `research_runs.stage` (and `.phase`/`.deep`) the instant a run settles
   *  (`completeResearchRun`, `src/research/store.ts`), so the finished-state
   *  strip reconstructs the final stage itself from the run's own durable
   *  stage HISTORY instead — see the `effectiveStage` derivation below. */
  stage?: ResearchStageKey;
  /** FRONT-1578: when the run started (ISO) — drives the "Running for…"
   *  elapsed-time readout. Absent renders no elapsed readout. */
  startedAt?: string;
  /** FRONT-1601 FIX 2: when the run finished (ISO, `ResearchRun.completedAt`)
   *  — freezes the elapsed clock at the run's own fixed duration once
   *  `finished` is true, instead of a live counter that never stops under a
   *  "Research finished" headline. Absent renders no elapsed readout in the
   *  finished state, same absence rule as `startedAt`. */
  completedAt?: string;
  /** FRONT-1578 review finding 2: whether the RUN ITSELF is known to be on
   *  the deep pipeline (`ResearchRun.deep`) — one of three deep-evidence
   *  sources the strip consults before it will show the two deep-only steps
   *  (Reviewing gaps, Filling gaps); see `hasDeepEvidence`. Absent/false
   *  means "not yet known to be deep," never "confirmed standard." */
  deep?: boolean;
  /** FRONT-1601: durable flag from the run row — set once "Finish now" has
   *  been requested for this run, null/absent otherwise. Drives
   *  {@link finishNowState}'s `"requested"` (control visible but inert)
   *  vs `"available"` split; see that function for the full rule. */
  finishRequestedAt?: string | null;
}

/**
 * FRONT-1601 FIX 7: `onShowBriefing` is the finished panel's only action —
 * a caller that sets `finished` without also supplying it would render a
 * primary button with nothing behind the click. A discriminated union on
 * `finished` turns that into a compile error instead of a doc comment
 * ("Required when `finished` is true") nobody is forced to read.
 */
export type ChatResearchBannerProps =
  | (ChatResearchBannerBaseProps & {
      /** FRONT-1601: true once this run has settled `completed` AND the
       *  panel was open at that moment — see `research-finished-notice.ts`'s
       *  own rules for exactly when. Swaps the "Currently…" headline and the
       *  Stop/Finish-now controls for a completion message and a single
       *  "Show me the result" action; elapsed time, search/source counts,
       *  work log and sources render exactly as they do for a running run —
       *  this is a state change to the same panel, not a redesign of it. The
       *  one other thing it touches is the stage strip: nothing carries
       *  `aria-current` or motion any more, and a step this run actually
       *  skipped (or never reached) still reads `skipped`, never `done` —
       *  showing it as done would claim work the run never performed (see
       *  {@link stageStatuses}). Dropping the "current" reading is the same
       *  contradiction-avoidance the bar's own badge already does two lines
       *  up. */
      finished?: false;
      onShowBriefing?: () => void;
    })
  | (ChatResearchBannerBaseProps & {
      finished: true;
      /** Collapses the panel back down, revealing the conversation
       *  underneath — the same action the panel's own bar and Escape
       *  already perform, just reachable without hunting for either.
       *  Required (see the union this belongs to) — `finished` with no way
       *  to act on it would be a dead primary button. */
      onShowBriefing: () => void;
    });

export function ChatResearchBanner(props: ChatResearchBannerProps) {
  const {
    show,
    expanded = false,
    onToggle,
    phase,
    runId,
    stage,
    startedAt,
    completedAt,
    deep,
    finishRequestedAt,
    finished = false,
    onShowBriefing,
  } = props;
  const active = show && runId != null;
  const activeRunId = active ? runId : undefined;
  // Polling follows what is ON SCREEN. Both queries still fetch once as soon
  // as there's a run id, so opening the panel mid-run shows the whole log
  // immediately instead of an empty box; only the 3s refresh is tied to the
  // panel being open, because a collapsed bar renders nothing either poll
  // feeds.
  // FRONT-1601 FIX 5: also gated on `!finished` — a completed run can never
  // change again, so polling its timeline/citations every 3s forever (the
  // panel used to unmount entirely at completion, before FRONT-1601 kept it
  // up in a finished state) would poll a dead run indefinitely.
  const live = active && expanded && !finished;
  // FRONT-1578 review finding 1: `isSuccess` is the query's OWN "has this
  // settled at least once" signal — never inferred from `data` being an
  // empty array, which is indistinguishable from "hasn't loaded yet." See
  // `stageStatuses`.
  const { data: events = [], isSuccess: timelineLoaded } = useResearchTimeline(
    activeRunId,
    { live },
  );
  const { data: citations = [] } = useResearchCitations(activeRunId, { live });
  const sourceCount = citations.length;
  const searchCount = events.filter((event) => event.kind === 'search').length;
  const cancelResearch = useCancelResearch();
  const finishResearch = useFinishResearch();

  // FRONT-1578 review finding 2: is this run KNOWN to be on the deep
  // pipeline? Three independent sources, any one of which is sufficient —
  // the run's own flag (covers the common case: the CURRENT stage is
  // already deep-only evidence, e.g. `stage === 'reviewing'`), a stage-event
  // in the history carrying the same flag (covers a deep run that has since
  // moved past its own deep-only phase, e.g. now `writing`), or a
  // `gap-check` timeline event (deep.ts's own gap-review, logged
  // independently of any phase label). Never inferred from anything else —
  // a false positive here shows a standard run two stages it can never
  // reach.
  const hasDeepEvidence =
    deep === true ||
    events.some((event) => event.deep === true || event.kind === 'gap-check');
  const displayedStages = hasDeepEvidence
    ? STAGE_ORDER
    : STAGE_ORDER.filter((key) => !DEEP_ONLY_DISPLAY_STAGES.has(key));

  // FRONT-1601: "Finish now" is deliberately gated on the run's own `deep`
  // flag, NOT the looser `hasDeepEvidence` above — a stage/gap-check event
  // is evidence the run reached a deep-only STAGE in the past, but the
  // control is about what the engine can still do RIGHT NOW, which only the
  // run row's live `deep` state answers. See `finish-now-gate.ts`.
  const finishState = finishNowState({ runId, deep, sourceCount, finishRequestedAt, stage });

  // FRONT-1578 review finding 5: the interval only runs while EXPANDED — for
  // most of a long run (collapsed, after the auto-collapse-on-send) nothing
  // reads `now`, so ticking it every second would only cost a re-render (and
  // re-computing the stage statuses and event filters over up to 300 rows)
  // to update a string nothing on screen shows. Seeded from `Date.now()` at
  // mount (`useState`'s lazy initializer runs on the FIRST render, unlike an
  // effect), so the very first paint already shows a correct value even in
  // this repo's effect-free test environment.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // FRONT-1601 FIX 2: stop ticking once finished too — a completed run's
    // duration is a fixed fact, so a live interval would only cost a
    // re-render a second to update a number the finished readout doesn't
    // even read any more (see `elapsedLabel` below, which switches to
    // `completedAt` instead of `now` the instant `finished` is true).
    if (!startedAt || !expanded || finished) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt, expanded, finished]);
  // FRONT-1601 FIX 2: once finished, the run's duration is FIXED —
  // `completedAt - startedAt` — never `now - startedAt`, which would keep
  // climbing for as long as the panel stayed open under a "Research
  // finished" headline. Falls back to the frozen `now` (the interval above
  // has already stopped ticking it by the time this reads `finished`) only
  // for an older/foreign row that somehow lacks `completedAt`, so the
  // readout still shows something close rather than nothing. `formatElapsed`
  // returns `''` for a non-finite duration (finding 9) — already falsy, so
  // the render check below (`elapsedLabel ? … : null`) correctly renders
  // nothing rather than a label with no value.
  const elapsedLabel = startedAt
    ? formatElapsed(
        (finished && completedAt ? new Date(completedAt).getTime() : now) -
          new Date(startedAt).getTime(),
      )
    : null;

  // FRONT-1601 FIX 1 (BLOCKER): `stage` (the prop) is `undefined` for every
  // FINISHED run — the backend nulls it, along with `phase`/`deep`, the
  // instant a run settles (`completeResearchRun`, `src/research/store.ts`),
  // deliberately, so a finished panel never keeps reading "Writing the
  // briefing…" forever. Left alone, that made `stageStatuses` return `null`
  // and the WHOLE strip vanish the moment a run finished — the "skipped
  // stays skipped, nothing current" correction on that function was
  // unreachable code for the one case it exists to handle. Reconstruct the
  // FINAL stage from the run's own durable stage HISTORY instead: the last
  // `kind: 'stage'` timeline row this component already loads. Only used
  // once `finished` — a running run keeps reading the live `stage` prop
  // exactly as before, so the running case is byte-for-byte untouched.
  //
  // `hasDeepEvidence` above needs no equivalent fix: it already scans the
  // full `events` array rather than trusting the `deep` prop, so restoring
  // the stage here also restores the correct deep-vs-standard step SET for
  // free — there is nothing further to derive for `deep`.
  const lastStageEvent = [...events]
    .reverse()
    .find(
      (event): event is ResearchTimelineEvent & { stage: ResearchStageKey } =>
        event.kind === 'stage' && event.stage != null,
    );
  const effectiveStage = finished ? lastStageEvent?.stage : stage;
  const stageStatusMap = stageStatuses(effectiveStage, events, timelineLoaded, finished);

  // Auto-scroll the work-log list to its latest row on every new event, so
  // "newest last" in the DOM also means "newest visible" without the user
  // having to scroll down themselves. `renderToStaticMarkup` (this repo's
  // frontend tests have no DOM) never runs effects, so this is inert there —
  // markup/order assertions are unaffected either way.
  const logRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events.length, expanded]);

  if (!show) return null;

  /* The cobalt identity bar — the whole thing is the expand/collapse control,
     so there is no small caret to hunt for. It renders identically in both
     states (same height, same position) so opening the panel reads as the
     detail sliding out from under a bar that stayed put. */
  const bar = (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={
        expanded
          ? 'Collapse the deep research details'
          : 'Expand the deep research details'
      }
      disabled={!onToggle}
      className="flex w-full shrink-0 items-center gap-2.5 bg-cobalt-700 px-[18px] py-2.5 text-left text-white transition-colors hover:bg-cobalt-600 disabled:hover:bg-cobalt-700"
    >
      <MagnifyingGlass size={18} weight="bold" className="shrink-0" />
      <span className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.16em]">
        Deep Research
      </span>
      <span
        data-testid="research-status-badge"
        className={
          finished
            ? 'inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.06em] text-success'
            : 'inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.06em]'
        }
      >
        {/* FRONT-1601: once the run has settled `completed` this badge must
            stop claiming the run is still going — the body two lines below
            already swaps to "Research finished". Gated on the SAME `finished`
            prop the body reads, so the bar and the body can never disagree.
            Finished uses the design-system SUCCESS token (never a raw green
            Tailwind scale, per CLAUDE.md) and drops the pulse entirely — a
            static check mark, not a slowed-down animation, since nothing is
            "alive" to breathe anymore. The running branch below is untouched:
            same literal classes, same cobalt-400 dot, same product-owner-
            specified colour and 2.4s breath (see the comment on that span). */}
        {finished ? (
          <CheckCircle size={10} weight="fill" className="shrink-0" aria-hidden="true" />
        ) : (
          /* The product owner specified this exact colour (#7FA8F2 /
             `cobalt-400`) for this dot — part of the surface's identity, not
             a state, so it is not the `success` token. Slowed to a 2.4s
             breath so it reads as "alive", not as an alarm. */
          <span className="size-1.5 rounded-full bg-cobalt-400 ring-2 ring-cobalt-400/25 motion-safe:animate-pulse [animation-duration:2.4s]" />
        )}
        {finished ? 'Finished' : 'In progress'}
      </span>
      {onToggle ? (
        <span className="ml-auto inline-flex shrink-0 items-center gap-2 text-[length:var(--text-xs)] font-medium text-white/85">
          <span className="hidden sm:inline">
            {expanded ? 'Click to collapse' : 'Click to expand for more details'}
          </span>
          {expanded ? (
            <span className="rounded border border-white/30 px-1.5 py-px text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.06em] text-white/80">
              Esc
            </span>
          ) : null}
          {expanded ? (
            <CaretUp size={15} weight="bold" />
          ) : (
            <CaretDown size={15} weight="bold" />
          )}
        </span>
      ) : null}
    </button>
  );

  if (!expanded) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="shrink-0 overflow-hidden border-b border-cobalt-800/30 dark:border-cobalt-800"
      >
        {bar}
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="research-details"
      /* Covers the conversation, not the whole app: the caller's `relative`
         wrapper spans exactly the banner + the scrolling column, so the chat's
         own header and composer stay reachable underneath. */
      className="absolute inset-0 z-20 flex flex-col overflow-hidden bg-cobalt-50/95 backdrop-blur-sm dark:bg-cobalt-950/95"
    >
      {bar}

      <div className="flex min-h-0 flex-1 flex-col gap-5 px-6 py-6 sm:px-8">
        {/* Where the run stands right now, and the way to call it off. */}
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            {/* FRONT-1601: the finished state replaces "Currently…" with a
                completion message — everything below this (the activity
                readout, stage strip, work log, sources) is untouched, so the
                run's own facts stay exactly where they already were. */}
            {finished ? (
              <div data-testid="research-finished">
                <p className="inline-flex items-center gap-1.5 text-[length:var(--text-xl)] font-bold leading-tight text-success">
                  {/* FRONT-1601 FIX 7: decorative, like the badge's own check
                      mark two sections up — the adjacent text already says
                      "finished", so this icon adds nothing a screen reader
                      needs. Was inconsistent with the badge before this fix
                      (badge: aria-hidden; this one: not). */}
                  <CheckCircle size={20} weight="fill" className="shrink-0" aria-hidden="true" />
                  Research finished
                </p>
                <p className="mt-1 text-[length:var(--text-sm)] text-cobalt-800/90 dark:text-cobalt-200/80">
                  Your briefing is in the chat behind this panel.
                </p>
              </div>
            ) : (
              <>
                <p className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.16em] text-cobalt-700/80 dark:text-cobalt-300/70">
                  Currently
                </p>
                {phase ? (
                  <p
                    data-testid="research-phase"
                    className="text-[length:var(--text-xl)] font-bold leading-tight text-cobalt-900 dark:text-cobalt-100"
                  >
                    {phase}
                  </p>
                ) : (
                  <p className="text-[length:var(--text-xl)] font-bold leading-tight text-cobalt-900/70 dark:text-cobalt-100/70">
                    Getting started…
                  </p>
                )}
              </>
            )}
            {/* FRONT-1578: visible activity so the panel never reads as idle
                — the elapsed clock keeps moving even in the gaps between
                events, and the two counts make "is this doing anything"
                answerable at a glance. `sourceCount` is the SAME value the
                Sources card below counts — never a second tally of it.
                aria-live="off": FRONT-1578 review finding 4 — same
                precedent as the work-log list below (the parent's own
                role="status" aria-live="polite" already announces the stage
                line, which is the summary a screen-reader user needs;
                without this, the elapsed clock re-announces itself every
                single second it ticks). */}
            <div
              aria-live="off"
              className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[length:var(--text-xs)] text-cobalt-700/70 dark:text-cobalt-300/60"
            >
              {elapsedLabel ? (
                <span data-testid="research-elapsed">
                  {/* FRONT-1601 FIX 2: "Running for" is a live-progress claim
                      — false the instant the run is finished, where the
                      duration is a fixed, past fact instead. */}
                  {finished ? `Took ${elapsedLabel}` : `Running for ${elapsedLabel}`}
                </span>
              ) : null}
              <span data-testid="research-search-count">
                {searchCount} search{searchCount === 1 ? '' : 'es'}
              </span>
              <span data-testid="research-source-count">
                {sourceCount} source{sourceCount === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* FRONT-1601: finished — there is nothing left to stop or
                finish, so neither control renders; a dead control is worse
                than none. The one action on offer is leaving. */}
            {finished ? (
              <Button
                type="button"
                variant="default"
                size="sm"
                data-testid="research-show-briefing"
                // WCAG 2.5.3 (same precedent as Finish now below): the
                // accessible name starts with the exact visible label so a
                // voice-control user saying "click Show me the result"
                // still matches it.
                aria-label="Show me the result — collapse this panel and reveal it in the chat"
                onClick={onShowBriefing}
                className="shrink-0"
              >
                {/* FRONT-1601 FIX 7: decorative, same reasoning as the
                    headline's own check mark above — the visible label
                    already says "Show me the result", and the button's own
                    aria-label already covers the accessible name. */}
                <CheckCircle size={15} weight="bold" aria-hidden="true" />
                Show me the result
              </Button>
            ) : (
              <>
                {/* FRONT-1601: the softer of the two actions, so it sits to the
                    LEFT of Stop — cutting a run short by asking it to wrap up
                    with what it has is a gentler ask than stopping it outright.
                    Hidden entirely rather than disabled-on-no-run/non-deep/no-
                    sources (see `finish-now-gate.ts`) — a control that can never
                    be honoured shouldn't be visible at all. Once requested it
                    stays on screen but inert (`"requested"`), so the user sees
                    their ask registered instead of a button that looks like it
                    did nothing on a second click. */}
                {finishState !== 'hidden' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="research-finish-now"
                    tooltip="Stop searching and write the briefing from what has been found so far"
                    // FIX 9 (WCAG 2.5.3, accessible name must contain the visible
                    // label): starts with the exact visible text below so a
                    // voice-control user saying "click Finish now" can match it,
                    // and reflects state so a screen-reader user is told the
                    // request registered — the sibling Stop button already does
                    // this (its aria-label starts with "Stop").
                    aria-label={
                      finishState === 'requested'
                        ? 'Finishing… — this research is wrapping up now'
                        : 'Finish now — stop searching and write up what this research has'
                    }
                    disabled={finishState === 'requested' || finishResearch.isPending}
                    onClick={() => runId != null && finishResearch.mutate({ runId })}
                    className="shrink-0 border-cobalt-300 bg-white text-cobalt-700 hover:bg-cobalt-100 dark:border-cobalt-700 dark:bg-cobalt-900 dark:text-cobalt-200 dark:hover:bg-cobalt-800"
                  >
                    <FlagCheckered size={15} weight="bold" />
                    {finishState === 'requested' ? 'Finishing…' : 'Finish now'}
                  </Button>
                )}
                {/* Only once there is a real run to stop. In the brief optimistic
                    window after approval there is no run row yet, and a Stop button
                    that cannot stop anything is worse than no button. */}
                {runId != null && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    tooltip="Stop this deep research run"
                    aria-label="Stop this deep research run"
                    disabled={cancelResearch.isPending}
                    onClick={() => cancelResearch.mutate({ runId })}
                    className="shrink-0 border-cobalt-300 bg-white text-cobalt-700 hover:bg-cobalt-100 dark:border-cobalt-700 dark:bg-cobalt-900 dark:text-cobalt-200 dark:hover:bg-cobalt-800"
                  >
                    <StopCircle size={15} weight="bold" />
                    Stop
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* FRONT-1578: the stage strip — up to five real stages, in order,
            so progress reads at a glance instead of only as one sentence of
            prose. Absent `stage` (an older run, or a label this build
            doesn't recognise), or while the timeline history hasn't settled
            yet, renders no strip at all — see {@link stageStatuses}, which
            is the one place "done" vs "skipped" is decided, off the run's
            own stage history, never off position alone (or, once `finished`
            is true, drops the "current" reading — a genuinely skipped or
            never-reached step still reads `skipped`, never `done` —
            FRONT-1601, same function, same rule). The two deep-only steps
            (Reviewing gaps, Filling gaps) are additionally left out of
            `displayedStages` entirely for a run not known to be on the deep
            pipeline (FRONT-1578 review finding 2, `hasDeepEvidence`) — a
            standard run can never reach them. */}
        {stageStatusMap ? (
          <ol
            data-testid="research-stages"
            aria-label="Research stages"
            className="flex flex-wrap items-center gap-1.5"
          >
            {displayedStages.map((key) => {
              const status = stageStatusMap[key];
              const isNow = status === 'now';
              return (
                <li
                  key={key}
                  data-testid={`research-stage-${key}`}
                  aria-current={isNow ? 'step' : undefined}
                  className={cn(
                    'relative flex items-center gap-1.5 overflow-hidden rounded-full px-2.5 py-1 text-[length:var(--text-2xs)] font-semibold tracking-[0.02em] transition-colors',
                    status === 'now' &&
                      'bg-cobalt-600 text-white shadow-sm dark:bg-cobalt-500',
                    status === 'done' &&
                      'bg-cobalt-100 text-cobalt-800 dark:bg-cobalt-900/60 dark:text-cobalt-200',
                    status === 'skipped' &&
                      'text-cobalt-700/40 line-through decoration-cobalt-700/30 dark:text-cobalt-300/30 dark:decoration-cobalt-300/20',
                    status === 'upcoming' &&
                      'border border-dashed border-cobalt-300/60 text-cobalt-700/60 dark:border-cobalt-700/50 dark:text-cobalt-300/50',
                    // The current step's own motion: a soft sheen sweeping it
                    // (~2s loop) and — planning specifically, the product
                    // owner's own callout, since it's the longest stretch
                    // where nothing else on the panel moves — a slightly more
                    // prominent breathing glow at the same 2.4s cadence
                    // already established for this surface's "In progress"
                    // dot. Both keyframes live in app.css; `motion-safe:` is
                    // what gates them, so a reduced-motion viewer sees a
                    // plain static highlighted pill and nothing else.
                    key === 'planning' &&
                      isNow &&
                      'motion-safe:animate-[research-planning-pulse_2.4s_ease-in-out_infinite]',
                  )}
                >
                  {isNow && (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-y-0 left-0 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/40 to-transparent motion-safe:animate-[research-stage-sheen_2s_linear_infinite]"
                    />
                  )}
                  {status === 'done' && (
                    <CheckCircle size={12} weight="fill" className="relative shrink-0 text-success" />
                  )}
                  {status === 'skipped' && (
                    <Minus size={10} weight="bold" className="relative shrink-0 opacity-60" />
                  )}
                  <span className="relative">{STAGE_LABELS[key]}</span>
                  {/* Status is carried visually (fill, tick, dim+strike,
                      dashed outline) but must also reach a screen reader,
                      which can't see colour or opacity. */}
                  {/* FRONT-1578: the live step reads "current step", NOT "in
                      progress" — the bar above already says "In progress" for
                      the run as a whole, and two elements inside one
                      role="status" saying the same words is both muddled read
                      aloud ("Deep Research In progress … Planning in
                      progress") and ambiguous to any locator scoped to the
                      banner. Observed in a real browser run: it broke
                      E2E-RESEARCH-01's existing bar assertion. */}
                  <span className="sr-only">
                    {status === 'now'
                      ? ' — current step'
                      : status === 'done'
                        ? ' — done'
                        : status === 'skipped'
                          ? ' — skipped'
                          : ' — upcoming'}
                  </span>
                </li>
              );
            })}
          </ol>
        ) : null}

        {/* The one-time orientation. It lives here rather than in the bar
            because it is the answer to "what is this thing doing" — exactly
            the question that made the user open the panel. FRONT-1601
            FIX 2: hidden once finished — this text describes a run that is
            still going ("is running in the background", "will deliver…
            when it's done"), and its own final clause ("reopen it anytime")
            is false the moment the finished notice is actually dismissed.
            The finished headline just above already says where the
            briefing landed, so this has nothing left to add. */}
        {!finished && (
          <p className="max-w-2xl text-[length:var(--text-sm)] leading-relaxed text-cobalt-800/90 dark:text-cobalt-200/80">
            Deep research is running in the background. It will deliver the full
            briefing right here in this chat when it&apos;s done — and save it to
            your inbox too. Send a message and this view folds itself away on
            its own so you can keep chatting — the bar stays put and you can
            reopen it anytime.
          </p>
        )}

        {/* Work log and sources side by side on a wide chat, stacked on a
            narrow one. Each scrolls inside its own card so a 40-search run
            grows downward instead of pushing anything off screen. */}
        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-cobalt-200/80 bg-white/70 dark:border-cobalt-800/60 dark:bg-cobalt-900/30">
            <header className="flex shrink-0 items-center justify-between gap-3 border-b border-cobalt-200/70 px-4 py-2.5 dark:border-cobalt-800/60">
              <span className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.12em] text-cobalt-700/90 dark:text-cobalt-300/80">
                Work log
              </span>
              <span className="text-[length:var(--text-2xs)] text-cobalt-700/70 dark:text-cobalt-300/60">
                {events.length} step{events.length === 1 ? '' : 's'}
              </span>
            </header>
            {/* aria-live="off": the parent's role="status" aria-live="polite"
                already announces the stage line, which is the summary a
                screen-reader user needs — without this, every one of a
                40-search run's rows gets its own polite announcement as it
                streams in. */}
            {events.length > 0 ? (
              <ul
                ref={logRef}
                data-testid="research-work-log"
                aria-live="off"
                className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3"
              >
                {events.map((event) => {
                  const KindIcon = KIND_ICON[event.kind] ?? FALLBACK_KIND_ICON;
                  return (
                    <li
                      key={event.id}
                      className="flex items-start gap-2.5 text-[length:var(--text-sm)] leading-snug text-cobalt-800/90 dark:text-cobalt-200/80"
                    >
                      <KindIcon
                        size={14}
                        weight="bold"
                        className="mt-0.5 shrink-0 text-cobalt-600/70 dark:text-cobalt-400/60"
                      />
                      <span className="min-w-0">{event.label}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="flex-1 px-4 py-3 text-[length:var(--text-sm)] leading-snug text-cobalt-800/70 dark:text-cobalt-200/60">
                {/* FRONT-1601 FIX 6: "Getting started…" implies more is
                    coming — false once the run is finished, so a finished
                    run with an empty log (a rare edge case: settled before
                    logging anything) reads as a closed fact instead. */}
                {finished ? 'No steps were logged.' : 'Getting started…'}
              </p>
            )}
            {/* FRONT-1578 review finding 3: ADDITIVE, not an empty-state
                fallback. The engine's own `onPhase` callback writes BOTH the
                `research_runs.phase` column AND this run's `kind: "stage"`
                timeline row for "Planning…" together — so by the time this
                component can ever observe `stage === 'planning'`, the work
                log already has that one row in it, and the old `events.length
                === 0` gate above made this branch unreachable in practice.
                Planning is still genuinely the longest stretch where nothing
                ELSE moves (no search has run yet), so render this animated
                placeholder BELOW whatever the work log already shows, for as
                long as that holds — reusing the app's existing shimmer
                keyframe, not a new one — rather than only when the log
                happens to still be visually empty. */}
            {stage === 'planning' && searchCount === 0 && (
              <div
                data-testid="research-worklog-placeholder"
                aria-hidden="true"
                className="shrink-0 space-y-2.5 px-4 py-3.5"
              >
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{ width: `${72 - i * 16}%`, animationDelay: `${i * 180}ms` }}
                    className="h-3 rounded-full bg-cobalt-200/70 motion-safe:animate-[answer-skeleton-shimmer_1.5s_ease-in-out_infinite] dark:bg-cobalt-800/50"
                  />
                ))}
              </div>
            )}
          </section>

          {/* The sources taken in so far, in the order they were filed. The
              product ask is that the user can always see "which sources it
              takes in" — so this is a peer of the work log here, not another
              disclosure to click through. */}
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-cobalt-200/80 bg-white/70 dark:border-cobalt-800/60 dark:bg-cobalt-900/30">
            <header className="flex shrink-0 items-center justify-between gap-3 border-b border-cobalt-200/70 px-4 py-2.5 dark:border-cobalt-800/60">
              <span className="inline-flex items-center gap-1.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[0.12em] text-cobalt-700/90 dark:text-cobalt-300/80">
                <LinkIcon size={12} weight="bold" />
                Sources
              </span>
              <span className="text-[length:var(--text-2xs)] text-cobalt-700/70 dark:text-cobalt-300/60">
                {sourceCount} source{sourceCount === 1 ? '' : 's'} found
              </span>
            </header>
            {sourceCount > 0 ? (
              <ul
                data-testid="research-sources"
                aria-live="off"
                className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3"
              >
                {citations.map((c) => {
                  // FRONT-2036 fix round 1 (item 16, hardening): `c.url` is
                  // MODEL-authored (the research engine records whatever the
                  // search provider handed back) — a non-http(s) scheme
                  // (`javascript:`, …) must never reach an `<a href>`. Render
                  // the citation as plain text instead of dropping it, so an
                  // odd source still shows up, just not as a live link.
                  const safeHref = safeSourceHref(c.url);
                  return (
                    <li
                      key={c.id}
                      className="text-[length:var(--text-xs)] leading-snug"
                    >
                      {safeHref ? (
                        <a
                          href={safeHref}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-cobalt-800 hover:underline dark:text-cobalt-200"
                        >
                          {c.title || c.url}
                        </a>
                      ) : (
                        <span className="font-medium text-cobalt-800 dark:text-cobalt-200">
                          {c.title || c.url}
                        </span>
                      )}
                      <span className="ml-2 text-cobalt-700/60 dark:text-cobalt-300/50">
                        {hostOf(c.url)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="flex-1 px-4 py-3 text-[length:var(--text-xs)] leading-snug text-cobalt-800/70 dark:text-cobalt-200/60">
                {/* FRONT-1601 FIX 6: "yet" implies more is coming — false
                    once the run is finished. */}
                {finished ? 'No sources were found.' : 'Nothing taken in yet.'}
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
